import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import { useQueryClient } from '@tanstack/react-query';
import {
  type TravellerMembershipDTO,
  useActivateCollectionMember,
  useInactivateCollectionMember,
  getGetTravellerByIDQueryKey,
  getGetTravellersForDepartmentQueryKey,
  getGetCollectionMembersQueryKey,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import { WarningDialog } from '@/components/WarningDialog';
import { canAccessDepartmentResource } from '@/helpers/departmentPermissions';
import {
  formatScheduledInactivation,
  getMembershipStatus,
} from '@/helpers/membershipStatus';
import type { ButtonProps } from '@/components/core/Button';

interface StudentMembershipStatusActionsProps {
  membership: TravellerMembershipDTO;
  travellerId: string;
  collectionId: string;
}

export const StudentMembershipStatusActions = ({
  membership,
  travellerId,
  collectionId,
}: StudentMembershipStatusActionsProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const inactivateCollectionMember = useInactivateCollectionMember();
  const activateCollectionMember = useActivateCollectionMember();

  const [inactivateDialogOpen, setInactivateDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [inactivateAtDate, setInactivateAtDate] = useState('');

  const canManage = useMemo(
    () => canAccessDepartmentResource(membership.department_id, true),
    [membership.department_id],
  );

  const status = getMembershipStatus(membership);
  const isInactive = status === 'inactive';
  const willInactivate = status === 'will-inactivate';
  const pendingInactivation = status === 'pending-inactivation';

  const statusText = useMemo(() => {
    if (isInactive) {
      return canManage
        ? formatMessage({ defaultMessage: 'Återaktivera' })
        : formatMessage({ defaultMessage: 'Inaktiv' });
    }
    if (willInactivate || pendingInactivation) {
      return formatScheduledInactivation(
        formatMessage,
        membership.inactivate_at,
      );
    }
    return formatMessage({ defaultMessage: 'Inaktivera' });
  }, [
    canManage,
    formatMessage,
    isInactive,
    membership.inactivate_at,
    pendingInactivation,
    willInactivate,
  ]);

  const { buttonColor, buttonVariant } = useMemo(() => {
    let color: ButtonProps['color'] = 'base';
    let variant: ButtonProps['variant'] = 'outline';

    if (isInactive) {
      color = 'red';
      variant = 'filled';
    } else if (willInactivate || pendingInactivation) {
      color = 'yellow';
      variant = 'filled';
    }

    return { buttonColor: color, buttonVariant: variant };
  }, [isInactive, pendingInactivation, willInactivate]);

  const invalidateRelatedQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getGetTravellerByIDQueryKey(travellerId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetTravellersForDepartmentQueryKey(
          membership.department_id,
        ),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetCollectionMembersQueryKey(collectionId),
      }),
    ]);
  }, [collectionId, membership.department_id, queryClient, travellerId]);

  const handleStatusButtonClick = useCallback(() => {
    if (!canManage) return;
    if (willInactivate || pendingInactivation || isInactive) {
      setReactivateDialogOpen(true);
    } else {
      setInactivateAtDate('');
      setInactivateDialogOpen(true);
    }
  }, [canManage, isInactive, pendingInactivation, willInactivate]);

  const onInactivateConfirm = useCallback(() => {
    const inactivateAt = inactivateAtDate
      ? DateTime.fromFormat(inactivateAtDate, 'yyyy-MM-dd').endOf('day').toISO()
      : undefined;

    inactivateCollectionMember.mutate(
      {
        collectionMemberID: membership.collection_member_id,
        data: {
          inactivate_at: inactivateAt ?? undefined,
        },
      },
      {
        onSuccess: async () => {
          await invalidateRelatedQueries();
          toast.success(
            {
              title: formatMessage({
                defaultMessage: 'Inaktivering genomförd',
              }),
            },
            { autoClose: 3000 },
          );
          setInactivateDialogOpen(false);
          setInactivateAtDate('');
        },
        onError: (error) => {
          console.error(error);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Ett fel uppstod',
              }),
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [
    formatMessage,
    inactivateAtDate,
    inactivateCollectionMember,
    invalidateRelatedQueries,
    membership.collection_member_id,
  ]);

  const onReactivateConfirm = useCallback(() => {
    activateCollectionMember.mutate(
      {
        collectionMemberID: membership.collection_member_id,
      },
      {
        onSuccess: async () => {
          await invalidateRelatedQueries();
          toast.success(
            {
              title: formatMessage({
                defaultMessage: 'Aktivering genomförd',
              }),
            },
            { autoClose: 3000 },
          );
          setReactivateDialogOpen(false);
        },
        onError: (error) => {
          console.error(error);
          toast.error(
            {
              title: formatMessage({
                defaultMessage: 'Ett fel uppstod',
              }),
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [
    activateCollectionMember,
    formatMessage,
    invalidateRelatedQueries,
    membership.collection_member_id,
  ]);

  const statusActionControl = useMemo(() => {
    if (canManage) {
      return (
        <Button
          title={statusText}
          variant={buttonVariant}
          color={buttonColor}
          onClick={handleStatusButtonClick}
          className="min-w-max shrink-0"
        >
          {statusText}
        </Button>
      );
    }
    if (isInactive) {
      return <span className="text-text-700">{statusText}</span>;
    }
    if (willInactivate || pendingInactivation) {
      return <span className="text-text-700">{statusText}</span>;
    }
    return null;
  }, [
    buttonColor,
    buttonVariant,
    canManage,
    handleStatusButtonClick,
    isInactive,
    pendingInactivation,
    statusText,
    willInactivate,
  ]);

  return (
    <>
      {statusActionControl}

      <WarningDialog
        title={formatMessage({ defaultMessage: 'Inaktivera' })}
        content={
          <div className="flex flex-col gap-6">
            <p>
              <FormattedMessage defaultMessage="Är du säker på att du vill inaktivera personen för denna barn- och ungdomskategori?" />
            </p>
            <p>
              <FormattedMessage defaultMessage="Alla biljetter som ställts ut för denna kategori blir genast makulerade." />
            </p>
            <p>
              <FormattedMessage defaultMessage="Det angivna datumet blir sista aktiva dagen." />
            </p>
            <Input
              id="inactivate-student-membership-date"
              label={formatMessage({ defaultMessage: 'Inaktiveringsdatum' })}
              type="date"
              value={inactivateAtDate}
              onChange={(e) => setInactivateAtDate(e.target.value)}
              min={DateTime.now().toFormat('yyyy-MM-dd')}
              helperText={formatMessage({
                defaultMessage:
                  'Om du inte anger ett datum sker inaktiveringen direkt.',
              })}
              helperTextReserveSpace
            />
          </div>
        }
        onCancel={() => {
          setInactivateDialogOpen(false);
          setInactivateAtDate('');
        }}
        buttonText={formatMessage({ defaultMessage: 'Inaktivera' })}
        loading={inactivateCollectionMember.isPending}
        onConfirm={onInactivateConfirm}
        open={inactivateDialogOpen}
      />
      <WarningDialog
        title={formatMessage({ defaultMessage: 'Återaktivera' })}
        content={
          <FormattedMessage defaultMessage="Är du säker på att du vill återaktivera personen för denna barn- och ungdomskategori?" />
        }
        onCancel={() => setReactivateDialogOpen(false)}
        buttonText={formatMessage({ defaultMessage: 'Återaktivera' })}
        loading={activateCollectionMember.isPending}
        onConfirm={onReactivateConfirm}
        open={reactivateDialogOpen}
      />
    </>
  );
};
